import {
  afterRenderEffect,
  Component,
  computed,
  inject,
  input,
  model,
  output,
  resource,
  signal,
  viewChild,
} from '@angular/core';
import { FormValueControl, ValidationError } from '@angular/forms/signals';
import { EditorComponent } from '@tinymce/tinymce-angular';
import type { Editor, RawEditorOptions } from 'tinymce';

import { MergeTag } from '../plugins/mergetags/core/Types';
import { MergeTagsPlugin } from '../plugins/mergetags/Plugin';
import {
  RICH_TEXT_EDITOR_DEFAULTS,
  TINYMCE_BASE_URL,
} from './rich-text-editor.config';

/** Bundles TinyMCE as a lazy chunk, so <editor> never falls back to the keyless cloud CDN, which locks the editor. */
async function loadTinymce(): Promise<true> {
  await import('tinymce');
  // Registers `dgmergetags`; re-registering is harmless.
  MergeTagsPlugin();
  return true;
}

/**
 * TinyMCE rich-text field, bound with `[formField]` like an Apollo control.
 * Callers pass feature-specific TinyMCE `options`, merged over a compact default;
 * to offer `mergeTags`, list `dgmergetags` in `options.plugins` and `options.toolbar`.
 *
 * Renders its own error text and points the editing area at it. The label stays
 * with the caller: point its `for` at `inputId` and pass its text as `ariaLabel`,
 * since TinyMCE's iframe doesn't pick up a `<label for>`.
 */
@Component({
  selector: 'dgx-rich-text-editor',
  imports: [EditorComponent],
  templateUrl: './rich-text-editor.component.html',
})
export class RichTextEditorComponent implements FormValueControl<string> {
  private readonly baseUrl = inject(TINYMCE_BASE_URL);

  /** The editor's id, for the caller's `<label for>`. */
  public readonly inputId = input.required<string>();
  /** The editing area's accessible name; include "required" when it is. */
  public readonly ariaLabel = input.required<string>();
  /** Space-separated ids of extra help text for the editing area. */
  public readonly describedBy = input<string>();
  /** TinyMCE options merged over the defaults; read once, when the editor initialises. */
  public readonly options = input<RawEditorOptions>({});
  public readonly mergeTags = input<readonly MergeTag[]>([]);
  public readonly minHeight = input(200);

  /** The HTML content: the `FormValueControl` contract. */
  public readonly value = model('');
  public readonly errors = input<
    readonly ValidationError.WithOptionalFieldTree[]
  >([]);
  public readonly invalid = input(false);
  public readonly dirty = input(false);
  public readonly disabled = input(false);
  public readonly touch = output<void>();

  protected readonly tinymce = resource({ loader: loadTinymce });

  protected readonly errorId = computed(() => `${this.inputId()}-error`);

  // Gated on dirty, like `da-input`, so a rejected submit marks the field dirty to show it.
  protected readonly errorMessage = computed(() =>
    this.dirty() && this.invalid() ? (this.errors()[0]?.message ?? '') : ''
  );

  protected readonly config = computed<RawEditorOptions>(() => ({
    ...RICH_TEXT_EDITOR_DEFAULTS,
    base_url: this.baseUrl,
    skin_url: `${this.baseUrl}/skins/ui/apollo`,
    min_height: this.minHeight(),
    iframe_aria_text: this.ariaLabel(),
    // The iframe sits in this document, so it can reference the error text; the body inside it can't.
    iframe_attrs: {
      'aria-describedby': [this.errorId(), this.describedBy()]
        .filter(Boolean)
        .join(' '),
    },
    mergetags_list: this.mergeTags(),
    ...this.options(),
  }));

  protected readonly instance = signal<Editor | null>(null);

  private readonly editor = viewChild(EditorComponent);

  // What the editor holds, so its own edits aren't written back over the caret.
  private editorValue: string | null = null;

  public constructor() {
    afterRenderEffect(() => {
      const editor = this.editor();
      editor?.registerOnChange((html: string) => {
        this.editorValue = html;
        this.value.set(html);
      });
      editor?.registerOnTouched(() => this.touch.emit());
    });

    afterRenderEffect(() => {
      const editor = this.editor();
      const html = this.value() ?? '';
      if (editor && html !== this.editorValue) {
        this.editorValue = html;
        editor.writeValue(html);
      }
    });

    afterRenderEffect(() => {
      this.instance()
        ?.getBody()
        .setAttribute('aria-invalid', String(this.errorMessage() !== ''));
    });
  }

  /** Lets `focusBoundControl()` move focus into the editing area. */
  public focus(): void {
    this.instance()?.focus();
  }
}
